import {
  ArrowLeft,
  ChevronRight,
  Download,
  Eraser,
  FolderPlus,
  Sparkles,
  Upload,
  type LucideIcon
} from 'lucide-react'
import type { TagBundleImportResult } from '@axilon-apps/domains-ontology-ui-state'

import { CleanupOrphanTagsDialog } from '../../components/tags/CleanupOrphanTagsDialog'
import { Button } from '../../components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle
} from '../../components/ui/sheet'
import { TagBundleExportCard } from '../SettingsPage/components/TagBundleExportCard'
import { TagBundleImportCard } from '../SettingsPage/components/TagBundleImportCard'
import { TagAddForm } from './TagAddDialog'

export type TagBundleTool = 'home' | 'add' | 'classify' | 'cleanup' | 'export' | 'import'

export function tagBundleToolFromParam(value: string | null): TagBundleTool | null {
  return value === 'home' ||
    value === 'add' ||
    value === 'classify' ||
    value === 'cleanup' ||
    value === 'export' ||
    value === 'import'
    ? value
    : null
}

function ToolRow({
  icon: Icon,
  title,
  description,
  onClick,
  disabled = false,
  status,
  testId
}: {
  icon: LucideIcon
  title: string
  description: string
  onClick: () => void
  disabled?: boolean
  status?: string
  testId: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className="flex w-full items-center gap-3 rounded-lg border border-line bg-card px-3 py-3 text-left transition-colors hover:border-brand/30 hover:bg-brand-soft/40 disabled:cursor-not-allowed disabled:opacity-50"
    >
      <Icon className="size-4 shrink-0 text-brand" aria-hidden />
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium text-ink">{title}</span>
        <span className="mt-0.5 block text-11 text-ink-3">{description}</span>
      </span>
      {status ? <span className="shrink-0 text-11 text-ink-3">{status}</span> : null}
      <ChevronRight className="size-4 shrink-0 text-ink-3" aria-hidden />
    </button>
  )
}

export function TagBundleToolsDrawer({
  open,
  tool,
  onOpenChange,
  onToolChange,
  onImported,
  onTagCreated,
  onTagsCleanedUp,
  onClassifyTags,
  classifyDisabled,
  classifyProgress,
  classifyNotice,
  classifiableTagCount,
  canWriteTags,
  canClassifyTags,
  canMaintainBundles,
  showOrphanCleanup,
  allowReplaceImport
}: {
  open: boolean
  tool: TagBundleTool
  onOpenChange: (open: boolean) => void
  onToolChange: (tool: TagBundleTool) => void
  onImported: (result: TagBundleImportResult) => void
  onTagCreated: () => void
  onTagsCleanedUp: () => void
  onClassifyTags: () => void
  classifyDisabled: boolean
  classifyProgress: { done: number; total: number } | null
  classifyNotice: string | null
  classifiableTagCount: number
  canWriteTags: boolean
  canClassifyTags: boolean
  canMaintainBundles: boolean
  showOrphanCleanup: boolean
  allowReplaceImport: boolean
}) {
  const toolAvailable =
    tool === 'home' ||
    (tool === 'add' && canWriteTags) ||
    (tool === 'classify' && canClassifyTags) ||
    (tool === 'cleanup' && showOrphanCleanup) ||
    ((tool === 'export' || tool === 'import') && canMaintainBundles)
  const activeTool = toolAvailable ? tool : 'home'
  const detailTitle = {
    home: 'Tag tools',
    add: 'Add tag by path',
    classify: 'Classify tags',
    cleanup: 'Clean up unused tags',
    export: 'Export bundle',
    import: 'Import bundle'
  }[activeTool]
  const hasTools = canWriteTags || canClassifyTags || canMaintainBundles || showOrphanCleanup

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="gap-0 sm:max-w-2xl"
        data-testid="tag-bundle-tools-drawer"
      >
        <SheetHeader className="border-b border-line">
          {activeTool === 'home' ? (
            <SheetTitle>Tag tools</SheetTitle>
          ) : (
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label="Back to tag tools"
                onClick={() => onToolChange('home')}
              >
                <ArrowLeft />
              </Button>
              <SheetTitle>{detailTitle}</SheetTitle>
            </div>
          )}
          <SheetDescription>
            {activeTool === 'home'
              ? 'Manage tags, maintenance, and portable registry bundles.'
              : `${detailTitle} for this organization.`}
          </SheetDescription>
        </SheetHeader>

        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {activeTool === 'home' ? (
            <div className="flex flex-col gap-5">
              {canWriteTags || canClassifyTags ? (
                <section className="flex flex-col gap-2" aria-labelledby="tag-tools-manage">
                  <h3 id="tag-tools-manage" className="m-0 text-xs font-semibold text-ink-2">
                    Manage
                  </h3>
                  {canWriteTags ? (
                    <ToolRow
                      icon={FolderPlus}
                      title="Add tag by path"
                      description="Create a tag registration from a path and source URL."
                      onClick={() => onToolChange('add')}
                      testId="advanced-add-tag-button"
                    />
                  ) : null}
                  {canClassifyTags ? (
                    <ToolRow
                      icon={Sparkles}
                      title="Classify tags"
                      description="Run the classifier over tags on the current inventory page."
                      onClick={() => onToolChange('classify')}
                      status={
                        classifyProgress
                          ? `${classifyProgress.done}/${classifyProgress.total}`
                          : undefined
                      }
                      testId="classify-tags-button"
                    />
                  ) : null}
                </section>
              ) : null}

              {showOrphanCleanup ? (
                <section className="flex flex-col gap-2" aria-labelledby="tag-tools-maintenance">
                  <h3 id="tag-tools-maintenance" className="m-0 text-xs font-semibold text-ink-2">
                    Maintenance
                  </h3>
                  <ToolRow
                    icon={Eraser}
                    title="Clean up unused tags"
                    description="Remove registrations that no longer have a matching graph tag."
                    onClick={() => onToolChange('cleanup')}
                    testId="cleanup-orphan-tags-button"
                  />
                </section>
              ) : null}

              {canMaintainBundles ? (
                <section className="flex flex-col gap-2" aria-labelledby="tag-tools-data">
                  <h3 id="tag-tools-data" className="m-0 text-xs font-semibold text-ink-2">
                    Import and export
                  </h3>
                  <ToolRow
                    icon={Download}
                    title="Export bundle"
                    description="Download a portable copy of this organization's tag registry."
                    onClick={() => onToolChange('export')}
                    testId="tag-bundle-export-tool"
                  />
                  <ToolRow
                    icon={Upload}
                    title="Import bundle"
                    description="Analyze and import a portable tag registry bundle."
                    onClick={() => onToolChange('import')}
                    testId="tag-bundle-import-tool"
                  />
                </section>
              ) : null}

              {!hasTools ? (
                <p className="m-0 text-sm text-ink-3">
                  You do not have permission to use tag management tools.
                </p>
              ) : null}
            </div>
          ) : activeTool === 'add' ? (
            <TagAddForm onCreated={onTagCreated} />
          ) : activeTool === 'classify' ? (
            <div className="flex flex-col gap-4">
              <p className="m-0 text-sm text-ink-3">
                Run the classifier over the graph-backed tags on the current inventory page.
              </p>
              <div className="rounded-lg border border-line bg-card px-3 py-3 text-sm text-ink-2">
                {classifyProgress
                  ? `Classifying ${classifyProgress.done.toLocaleString()} of ${classifyProgress.total.toLocaleString()} tags…`
                  : `${classifiableTagCount.toLocaleString()} tag${classifiableTagCount === 1 ? '' : 's'} available on this page.`}
              </div>
              {classifyNotice ? (
                <div className="rounded-md bg-bg-sunk p-2.5 text-sm text-ink-2" role="status">
                  {classifyNotice}
                </div>
              ) : null}
              <Button
                className="self-start"
                onClick={onClassifyTags}
                disabled={classifyDisabled}
                data-testid="run-classify-tags"
              >
                {classifyProgress ? 'Classifying…' : 'Classify current page'}
              </Button>
            </div>
          ) : activeTool === 'cleanup' ? (
            <CleanupOrphanTagsDialog
              open={open}
              onOpenChange={onOpenChange}
              onCleanedUp={onTagsCleanedUp}
              embedded
            />
          ) : activeTool === 'export' ? (
            <TagBundleExportCard embedded />
          ) : (
            <TagBundleImportCard
              embedded
              allowReplaceMode={allowReplaceImport}
              onImported={onImported}
            />
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
